import type { HermesTomlConfig } from '../schema/hermes-toml.js';
import { configurationNix, FLAKE_NIX, type DomainConfig } from './templates.js';

export function generateConfigurationNix(config: HermesTomlConfig, sshPublicKey?: string): string {
  const domain: DomainConfig | undefined = config.domain
    ? { name: config.domain.name, upstream_port: config.domain.upstream_port }
    : undefined;
  return configurationNix(config.cloud.provider, sshPublicKey, config.hermes.cachix, domain);
}

export function generateFlakeNix(): string {
  return FLAKE_NIX;
}

/**
 * Generate hermes.nix from the validated hermes.toml config.
 *
 * Always emits services.hermes-agent.{enable, configFile, environmentFiles}.
 * Conditionally emits .documents and .environment when those tables are
 * non-empty. The user's nix_extra (when set) is uploaded as
 * /etc/nixos/hermes.extra.nix and pulled in by flake.nix's modules list
 * via pathExists — NOT via an `imports = [...]` line in hermes.nix
 * itself, so this generator stays simple.
 *
 * environmentFiles always references config.sops.secrets."hermes-env".path
 * because configuration.nix always declares that secret. The dotenv file
 * is always present (init bootstraps an empty placeholder one) and
 * sops-nix decrypts it at activation.
 */
export function generateHermesNix(config: HermesTomlConfig): string {
  const lines: string[] = [];
  lines.push('{ config, pkgs, lib, ... }:');
  lines.push('{');
  lines.push('  services.hermes-agent = {');
  lines.push('    enable = true;');
  lines.push('    configFile = ./config.yaml;');
  lines.push('    environmentFiles = [ config.sops.secrets."hermes-env".path ];');

  const docEntries = Object.entries(config.hermes.documents);
  if (docEntries.length > 0) {
    lines.push('');
    lines.push('    documents = {');
    for (const [filename] of docEntries) {
      // The file is uploaded to /etc/nixos/<filename> by the orchestrator.
      // Inside hermes.nix the path is just ./<filename> — Nix path literals
      // resolve relative to the file containing them.
      lines.push('      "' + escapeNixString(filename) + '" = ' + nixPath('./' + filename) + ';');
    }
    lines.push('    };');
  }

  const envEntries = Object.entries(config.hermes.environment);
  if (envEntries.length > 0) {
    lines.push('');
    lines.push('    environment = {');
    for (const [key, value] of envEntries) {
      lines.push('      ' + key + ' = "' + escapeNixString(value) + '";');
    }
    lines.push('    };');
  }

  lines.push('  };');

  const watchdog = config.hermes.watchdog;
  if (watchdog?.enabled) {
    const cooldownSecs = watchdog.cooldown_min * 60;
    lines.push('');
    lines.push('  # --- Gateway watchdog ([hermes.watchdog]) ---');
    lines.push('  # Covers the zombie case Restart=always cannot see: the hermes-agent');
    lines.push('  # process alive but the Discord websocket stuck in discord.py\'s resume');
    lines.push('  # loop (WSServerHandshakeError against a dead session gateway host,');
    lines.push('  # observed 2026-08-31). Restarts the service when handshake failures');
    lines.push('  # appear in the journal, at most once per cooldown.');
    lines.push('  systemd.services.hermes-gateway-watchdog = {');
    lines.push('    description = "Restart hermes-agent when the Discord gateway websocket is stuck";');
    lines.push('    serviceConfig.Type = "oneshot";');
    lines.push('    path = [ pkgs.systemd pkgs.gnugrep pkgs.coreutils ];');
    lines.push("    script = ''");
    lines.push('      systemctl is-active --quiet hermes-agent || exit 0');
    lines.push('      stamp=/run/hermes-gateway-watchdog.last-restart');
    lines.push('      now=$(date +%s)');
    lines.push('      if [ -f "$stamp" ] && [ $(( now - $(stat -c %Y "$stamp") )) -lt ' + cooldownSecs + ' ]; then');
    lines.push('        exit 0');
    lines.push('      fi');
    lines.push('      if journalctl -u hermes-agent --since "-' + watchdog.window_min + 'min" --no-pager -q | grep -q "WSServerHandshakeError"; then');
    lines.push('        echo "gateway handshake failures within ' + watchdog.window_min + 'min — restarting hermes-agent"');
    lines.push('        touch "$stamp"');
    lines.push('        systemctl restart hermes-agent');
    lines.push('      fi');
    lines.push("    '';");
    lines.push('  };');
    lines.push('  systemd.timers.hermes-gateway-watchdog = {');
    lines.push('    wantedBy = [ "timers.target" ];');
    lines.push('    timerConfig = {');
    lines.push('      OnBootSec = "' + watchdog.interval_min + 'min";');
    lines.push('      OnUnitActiveSec = "' + watchdog.interval_min + 'min";');
    lines.push('    };');
    lines.push('  };');
  }

  lines.push('');
  lines.push('  # Restart hermes-agent whenever sops-nix re-decrypts the secrets file.');
  lines.push('  # Without this, `secret set` + `update` re-decrypts the file on disk');
  lines.push('  # but the running process still has the old .env loaded from startup.');
  lines.push('  systemd.services.hermes-agent.restartTriggers = [');
  lines.push('    config.sops.secrets."hermes-env".sopsFile');
  lines.push('  ];');
  lines.push('}');
  lines.push('');
  return lines.join('\n');
}

/**
 * Validate a string going into a Nix path literal. Nix unquoted path
 * literals support a limited character set — anything else (spaces,
 * shell metacharacters, etc.) is invalid syntax. We validate at
 * generation time so users get a clear error instead of a confusing
 * Nix evaluation failure on the box.
 */
function nixPath(p: string): string {
  if (/[^\w./+-]/.test(p)) {
    throw new Error(
      'nix-gen: path "' + p + '" contains characters that are invalid in a ' +
      'Nix path literal. Use a path with only [A-Za-z0-9._+-/] characters.',
    );
  }
  if (p.startsWith('./') || p.startsWith('/')) return p;
  return './' + p;
}

/** Escape a string for inclusion in a Nix double-quoted string literal. */
function escapeNixString(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\$/g, '\\$');
}
